import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Play, X, BookOpen, ArrowUpRight } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import { api } from "@/lib/api";
import BannerImage from "@/components/BannerImage";
import { track } from "@/lib/analytics";

const CONECTADOS_BANNER =
  "https://customer-assets.emergentagent.com/job_lideranca-impacto/artifacts/ozrvschi_WhatsApp%20Image%202026-04-21%20at%2022.31.31.jpeg";

const CATEGORIES = [
  {
    key: "palavra",
    label: "Conectados pela Palavra",
    hint: "Reflexões profundas para a vida prática.",
  },
  {
    key: "instagram",
    label: "Mensagens do Instagram",
    hint: "Cortes curtos, com impacto direto.",
  },
  {
    key: "pregacoes",
    label: "Pregações (Igreja IECB Ágape)",
    hint: "Mensagens ministradas na igreja local.",
  },
];

function thumbFor(url) {
  const yt = url.match(/youtube\.com\/embed\/([\w-]+)/);
  if (yt) return `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg`;
  return null; // Instagram, etc, we use stylized card
}

function ReelCard({ v, onOpen }) {
  const thumb = thumbFor(v.video_url);
  const isInstagram = /instagram\.com/.test(v.video_url);
  return (
    <div
      data-testid={`video-card-${v.id}`}
      className="video-card text-left group w-full flex flex-col"
    >
      <button
        type="button"
        onClick={() => {
          track("video_open", { id: v.id, title: v.title, type: v.type });
          onOpen(v);
        }}
        data-testid={`video-card-open-${v.id}`}
        aria-label={`Assistir ${v.title}`}
        className="text-left w-full"
      >
        <div className="relative aspect-[9/16] overflow-hidden bg-[#0B1E36]">
          {thumb ? (
            <img
              src={thumb}
              alt={v.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:opacity-100 transition-opacity duration-500"
            />
          ) : (
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                background:
                  "radial-gradient(ellipse at 30% 20%, rgba(197,160,89,0.25) 0%, transparent 55%), linear-gradient(145deg, #0B1E36 0%, #13294B 55%, #0B1E36 100%)",
              }}
            >
              <div className="absolute inset-4 border border-[#C5A059]/20" />
              <div className="relative text-center px-6">
                <p className="text-[0.6rem] uppercase tracking-[0.3em] text-[#C5A059] font-medium">
                  {isInstagram ? "Instagram · @carlosmedeiros" : "Mensagem"}
                </p>
                <p className="mt-6 font-serif-display italic text-white/80 text-[1.15rem] leading-[1.35]">
                  {v.theme || "Reflexão"}
                </p>
              </div>
            </div>
          )}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(11,30,54,0.1) 0%, rgba(11,30,54,0.0) 45%, rgba(11,30,54,0.85) 100%)",
            }}
            aria-hidden
          />
          <div
            className="play-btn absolute w-14 h-14 rounded-full bg-[#C5A059] text-white flex items-center justify-center shadow-lg"
            style={{
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <Play size={18} strokeWidth={1.5} fill="currentColor" />
          </div>
          {v.duration && (
            <span className="absolute bottom-3 right-3 bg-black/70 text-white text-[0.68rem] px-2 py-1 tracking-wider">
              {v.duration}
            </span>
          )}
        </div>
        <div className="mt-6">
          <p className="text-[0.7rem] uppercase tracking-[0.3em] text-[#C5A059] font-medium">
            {v.theme || "Reflexão"}
          </p>
          <p className="mt-3 font-serif-display text-[1.25rem] md:text-[1.35rem] text-[#0B1E36] leading-[1.3] tracking-[-0.014em]">
            {v.impact_phrase || v.title}
          </p>
        </div>
      </button>

      {v.related_sermon_slug && (
        <Link
          to={`/pregacoes/${v.related_sermon_slug}`}
          data-testid={`video-card-related-sermon-${v.id}`}
          className="mt-4 inline-flex items-center gap-2 text-[0.72rem] uppercase tracking-[0.22em] text-[#0B1E36]/70 hover:text-[#C5A059] transition-colors duration-300 group/link self-start border-b border-[#0B1E36]/10 hover:border-[#C5A059]/60 pb-1.5"
          title={v.related_sermon_title || "Leia o sermão relacionado"}
        >
          <BookOpen size={13} strokeWidth={1.7} className="opacity-80" />
          <span>Leia o sermão relacionado</span>
          <ArrowUpRight
            size={13}
            strokeWidth={1.7}
            className="opacity-60 transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5"
          />
        </Link>
      )}
    </div>
  );
}

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState("palavra");
  const [modal, setModal] = useState(null);
  const r = useReveal();

  useEffect(() => {
    api
      .get("/videos")
      .then(({ data }) => setVideos(data))
      .catch(() => setVideos([]));
  }, []);

  const filtered = useMemo(
    () => videos.filter((v) => v.category === active).slice(0, 6),
    [videos, active]
  );

  return (
    <section
      id="videos"
      data-testid="videos-section"
      className="section-pad bg-mist"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div ref={r} className="reveal max-w-3xl">
          <div className="flex items-center mb-6">
            <span className="accent-rule" />
            <span className="eyebrow">Mensagens e Conteúdos</span>
          </div>
          <h2 className="font-serif-display text-[#0B1E36] text-[2.75rem] sm:text-5xl md:text-6xl lg:text-[4.75rem] leading-[1.02] tracking-[-0.028em] font-medium">
            A Palavra em <span className="italic text-[#C5A059]">movimento</span>.
          </h2>
          <p className="mt-10 text-[1.15rem] md:text-[1.3rem] text-[#6B7280] leading-[1.82] font-light max-w-2xl">
            Assista mensagens que fortalecem a fé e transformam a maneira de
            viver. Conteúdos breves, intencionais e profundos, para
            acompanhar o seu dia.
          </p>
        </div>

        <div className="mt-14" data-testid="videos-banner-wrap">
          <BannerImage
            src={CONECTADOS_BANNER}
            alt="Nosso Canal, Conectados pela Palavra, Podcast"
            testId="videos-banner"
          />
        </div>

        {/* Tabs */}
        <div className="mt-14 flex flex-col md:flex-row gap-0 border-b border-[#0B1E36]/10">
          {CATEGORIES.map((c) => {
            const isActive = active === c.key;
            return (
              <button
                key={c.key}
                onClick={() => setActive(c.key)}
                data-testid={`videos-tab-${c.key}`}
                className={`text-left px-6 py-5 transition-all duration-300 flex-1 border-b-2 ${
                  isActive
                    ? "border-[#C5A059] text-[#0B1E36]"
                    : "border-transparent text-[#111827]/60 hover:text-[#0B1E36]"
                }`}
              >
                <span className="block font-serif-display text-xl md:text-2xl tracking-[-0.012em]">
                  {c.label}
                </span>
                <span className="block mt-1 text-xs uppercase tracking-[0.22em] text-[#111827]/50">
                  {c.hint}
                </span>
              </button>
            );
          })}
        </div>

        {/* Vertical reel grid - generous spacing, max 4 per row */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-10 gap-y-16">
          {filtered.length === 0 ? (
            <div className="col-span-full text-[#6B7280] py-20 text-center">
              Nenhum vídeo nesta categoria ainda.
            </div>
          ) : (
            filtered.map((v) => <ReelCard key={v.id} v={v} onOpen={setModal} />)
          )}
        </div>
      </div>

      {/* Modal, vertical player */}
      {modal && (
        <div
          data-testid="video-modal"
          className="fixed inset-0 z-50 bg-[#0B1E36]/92 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setModal(null)}
        >
          <div
            className="relative w-full max-w-[420px]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setModal(null)}
              className="absolute -top-11 right-0 text-white/80 hover:text-[#C5A059] flex items-center gap-2 text-xs uppercase tracking-[0.22em]"
              data-testid="video-modal-close"
            >
              <X size={14} strokeWidth={1.5} />
              Fechar
            </button>

            <div className="bg-black aspect-[9/16] overflow-hidden">
              <iframe
                src={modal.video_url}
                title={modal.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>

            <div className="mt-4 bg-[#0B1E36] border border-white/10 px-5 py-4">
              <p className="text-[0.68rem] uppercase tracking-[0.28em] text-[#C5A059] font-medium">
                {modal.theme || "Reflexão"}
              </p>
              <p className="mt-2 font-serif-display text-white text-lg leading-snug tracking-[-0.012em]">
                {modal.impact_phrase || modal.title}
              </p>
              {modal.related_sermon_slug && (
                <Link
                  to={`/pregacoes/${modal.related_sermon_slug}`}
                  data-testid="video-modal-related-sermon"
                  onClick={() => setModal(null)}
                  className="mt-4 inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.24em] text-white/75 hover:text-[#C5A059] transition-colors duration-300 border-b border-white/15 hover:border-[#C5A059]/60 pb-1.5 group/modal-link"
                  title={modal.related_sermon_title || "Leia o sermão relacionado"}
                >
                  <BookOpen size={13} strokeWidth={1.7} />
                  <span>Leia o sermão relacionado</span>
                  <ArrowUpRight
                    size={13}
                    strokeWidth={1.7}
                    className="opacity-70 transition-transform duration-300 group-hover/modal-link:translate-x-0.5 group-hover/modal-link:-translate-y-0.5"
                  />
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
